import React from "react";

import { DealExtraDetailsIntegrationMark } from "manager/components/IntegrationMark";
import { FadeIn, Rotate } from "shared/components/animated";
import DropdownMenu from "shared/components/DropdownMenu";
import FlexContainer from "shared/components/FlexContainer";
import Icon from "shared/components/Icon";
import { ActionLink, IconLink } from "shared/components/Links";
import { Popover } from "shared/components/Popover";
import { ExtraStrongText } from "shared/components/Typography";
import { useVeroFlag } from "shared/utils/featureFlags";
import { VeroFlag } from "shared/utils/veroFlags";

import { DealUnitHeaderItems, UnitPopover } from "./blocks";
import { CosignBadge } from "./CosignBadge";
import { useDealHeaderInternal } from "./hooks";
import {
  AffordableBadge,
  DealHeaderContent,
  DealHeaderDropdownTrigger,
  InfoDark,
  MainText
} from "./styled";
import { DealHeaderDeal } from "./types";
import { isCanceledDeal, isDeclinedDeal, TitlePopoverWrapper } from "./utils";

export type DealHeaderPropsV2 = {
  deal: DealHeaderDeal;
  refetchDeal?: () => void;
  contactAll?: any;
  invite?: any;
  requestCountyRecords?: any;
  companyHasCosignEnabled?: boolean;
};

export const DealHeaderV2: React.FC<DealHeaderPropsV2> = ({
  deal,
  refetchDeal,
  contactAll,
  invite,
  requestCountyRecords,
  companyHasCosignEnabled
}) => {
  const useDealsListUiRedesign = useVeroFlag(VeroFlag.useDealsListUiRedesign);
  const showAffordableUnitBadgeFlag = useVeroFlag(
    VeroFlag.useShowAffordableUnitBadge
  );
  const showAffordableBadge =
    showAffordableUnitBadgeFlag && Boolean(deal?.unit?.isAffordable);
  const {
    dealHeaderTitleContainerRef,
    dropdownItems,
    extraContentRef,
    hasProperty,
    hasSource,
    hasUnit,
    isExpanded,
    isMobile,
    mainTitle,
    pdfProps,
    propertyAddress,
    status,
    unitHeaderTitle,
    items,
    toggleExpandedSection,
    assignUnit,
    managerPropertyUrl,
    shouldHideDownloadPDFButton,
  } = useDealHeaderInternal({ deal, refetchDeal, contactAll, invite, requestCountyRecords });

  return (
    <DealHeaderContent
      isCancelledDeal={isCanceledDeal(status)}
      isDeclinedDeal={isDeclinedDeal(status)}
      useDealsListUiRedesign={useDealsListUiRedesign}
      isMobile={isMobile}
    >
      <div
        className="deal-header-first-col"
        role="button"
        tabIndex={0}
        onClick={() => isMobile && toggleExpandedSection()}
        onKeyPress={() => null}
        style={{
          cursor: isMobile ? "pointer" : "default",
          position: "relative",
        }}
      >
        <FadeIn delay={100}>
          <div
            ref={dealHeaderTitleContainerRef}
            className={`deal-header-title-container ${!hasUnit ? "deal-header-title-no-unit" : ""
              }`}
          >
            {useDealsListUiRedesign ? (
              <>
                <MainText>Application #{deal?.id}</MainText>
                {!hasUnit && (
                  <div ref={extraContentRef}>
                    <ActionLink
                      className="deal-header-assign-unit"
                      onClick={assignUnit}
                    >
                      <ExtraStrongText>ASSIGN UNIT</ExtraStrongText>
                    </ActionLink>
                  </div>
                )}
                {companyHasCosignEnabled &&  <div style={{ marginLeft: "16px", display: "inline-flex" }}><CosignBadge
                  isEnabled={!!deal?.property?.hasCosignEnabled}
                  onOnboardClick={() => {
                    // TODO: Handle Cosign onboarding
                    console.log("Onboard with Cosign clicked");
                  }}
                  learnMoreUrl="#"
                /></div>}
                {showAffordableBadge && (
                  <AffordableBadge data-testid="affordable-badge">
                    Affordable Unit
                  </AffordableBadge>
                )}
              </>
            ) : (
              <>
                <TitlePopoverWrapper
                  title={unitHeaderTitle}
                  propertyAddress={propertyAddress}
                >
                  <MainText><a href={managerPropertyUrl}>{mainTitle}</a></MainText>

                </TitlePopoverWrapper>
                {showAffordableBadge && (
                  <AffordableBadge data-testid="affordable-badge">
                    Affordable Unit
                  </AffordableBadge>
                )}
                {companyHasCosignEnabled && <div style={{ marginLeft: "16px", display: "inline-flex" }}>
                  <CosignBadge
                  isEnabled={!!deal?.property?.hasCosignEnabled}
                  onOnboardClick={() => {
                    // TODO: Handle Cosign onboarding
                    console.log("Onboard with Cosign clicked");
                  }}
                /></div>}
                {(hasUnit || hasProperty) && (
                  <DealExtraDetailsIntegrationMark
                    property={deal.property}
                    unit={deal.unit}
                    deal={deal}
                    dark
                    markRef={extraContentRef}
                  />
                )}
                {!hasUnit && (
                  <div ref={extraContentRef}>
                    <ActionLink
                      className="deal-header-assign-unit"
                      onClick={assignUnit}
                    >
                      <ExtraStrongText>ASSIGN UNIT</ExtraStrongText>
                    </ActionLink>
                  </div>
                )}
                {(hasUnit || hasSource) && isMobile && (
                  <FlexContainer
                    justifyContent="flex-end"
                    style={{ marginLeft: 10 }}
                  >
                    <Rotate degrees={-180} on={isExpanded}>
                      <Icon.DropdownButtonChevronDownIcon
                        className="dropdown-chevron"
                        onClick={toggleExpandedSection}
                      />
                    </Rotate>
                  </FlexContainer>
                )}
              </>
            )}
          </div>
        </FadeIn>
        {/* <div style={{ flex: 1 }}><FadeIn delay={150}>
          <CosignBadge
            isEnabled={!!deal?.property?.hasCosignEnabled}
            onOnboardClick={() => {
              // TODO: Handle Cosign onboarding
              console.log("Onboard with Cosign clicked");
            }}
            learnMoreUrl="#"
          />
        </FadeIn></div> */}
        <FadeIn delay={150}>
          <FlexContainer alignItems="center">
            <DealUnitHeaderItems
              {...items}
              useDealsListUiRedesign={useDealsListUiRedesign}
              mainTitle={mainTitle}
              managerPropertyUrl={managerPropertyUrl}
              trailingExtra={
                !isMobile && !useDealsListUiRedesign ? (
                  <Popover
                    className="popover"
                    withoutOverlay
                    placement="bottom"
                    // @ts-ignore
                    trigger="hover"
                    content={<UnitPopover deal={deal} />}
                  >
                    <InfoDark />
                  </Popover>
                ) : undefined
              }
            />
          </FlexContainer>
        </FadeIn>
      </div>
      <div className="deal-header-second-col">
        <FadeIn delay={250}>
          <div className="deal-header-actions">
            {!useDealsListUiRedesign && !shouldHideDownloadPDFButton &&
              pdfProps.hasPermissionToDownloadPDF &&
              pdfProps.isDealPDFDownloadable && (
                <IconLink
                  onClick={pdfProps.downloadDealPDF}
                  Icon={
                    isCanceledDeal(status)
                      ? Icon.DownloadPdfDocGray
                      : Icon.DownloadPdfDoc
                  }
                  size="sm"
                  light
                  right
                  hoverableIcon={false}
                  disabled={pdfProps.isLoadingPDF}
                  className="deal-header-pdf-download"
                />
              )}
            <DropdownMenu placement="bottomRight" items={dropdownItems}>
              {useDealsListUiRedesign ? (
                <DealHeaderDropdownTrigger>
                  <Icon.DropdownIcon />
                </DealHeaderDropdownTrigger>
              ) : undefined}
            </DropdownMenu>
          </div>
        </FadeIn>
      </div>
    </DealHeaderContent>
  );
};
